import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RaffleParticipant } from "@/apps/raffle/types";
import { useRafflePageContext } from "./RafflePageContext";
import { ChevronDownIcon } from "./icons";
import cls from "./styles.module.scss";

const TOP_PARTICIPANTS_COUNT = 3;

type LeaderboardRowProps = {
  participant: RaffleParticipant;
  place: number;
};

const LeaderboardRow = ({ participant, place }: LeaderboardRowProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={[
        cls.leaderboardRow,
        participant.is_me ? cls.leaderboardRowMe : "",
      ].join(" ")}
    >
      <span className={cls.leaderboardPlace}>{place}</span>
      <span className={cls.leaderboardName}>{participant.name}</span>
      <span className={cls.leaderboardRides}>{participant.rides}</span>
    </div>
  );
};

export const RaffleLeaderboard = () => {
  const { t } = useTranslation();
  const { participants } = useRafflePageContext();
  const [isOpen, setIsOpen] = useState(false);
  const topParticipants = participants.slice(0, TOP_PARTICIPANTS_COUNT);
  const hiddenParticipants = participants.slice(TOP_PARTICIPANTS_COUNT);
  const meIndex = participants.findIndex((participant) => participant.is_me);
  const shouldShowFoldedMe =
    !isOpen && meIndex >= TOP_PARTICIPANTS_COUNT && participants[meIndex];

  if (participants.length === 0) return null;

  return (
    <section className={cls.leaderboardCard}>
      <div className={cls.leaderboardHeader}>
        <h2 className={cls.leaderboardTitle}>
          {t("raffle.leaderboard.title")}
        </h2>
        <p className={cls.leaderboardSubtitle}>
          {t("raffle.leaderboard.subtitle")}
        </p>
      </div>
      <div className={cls.leaderboardList}>
        {topParticipants.map((participant, index) => (
          <LeaderboardRow
            key={`${participant.rank}-${participant.name}`}
            participant={participant}
            place={index + 1}
          />
        ))}
      </div>
      <div
        className={[
          cls.leaderboardPanel,
          isOpen ? cls.leaderboardPanelOpen : "",
        ].join(" ")}
        aria-hidden={!isOpen}
      >
        <div className={cls.leaderboardList}>
          {hiddenParticipants.map((participant, index) => (
            <LeaderboardRow
              key={`${participant.rank}-${participant.name}`}
              participant={participant}
              place={index + TOP_PARTICIPANTS_COUNT + 1}
            />
          ))}
        </div>
      </div>
      {participants.length > TOP_PARTICIPANTS_COUNT && (
        <button
          type="button"
          className={cls.leaderboardToggle}
          onClick={() => setIsOpen((current) => !current)}
        >
          <span>
            {t(
              isOpen
                ? "raffle.leaderboard.hideAll"
                : "raffle.leaderboard.showAll"
            )}
          </span>
          <span
            className={[
              cls.leaderboardToggleIcon,
              isOpen ? cls.leaderboardToggleIconOpen : "",
            ].join(" ")}
          >
            <ChevronDownIcon />
          </span>
        </button>
      )}
      <div
        className={[
          cls.leaderboardMePanel,
          shouldShowFoldedMe ? cls.leaderboardMePanelVisible : "",
        ].join(" ")}
        aria-hidden={!shouldShowFoldedMe}
      >
        {meIndex >= 0 && (
          <div className={cls.leaderboardMeWrap}>
            <LeaderboardRow
              participant={participants[meIndex]}
              place={meIndex + 1}
            />
          </div>
        )}
      </div>
    </section>
  );
};
